import type { PlainMessage, Timestamp } from "@bufbuild/protobuf";

export type LockState = "normal" | "approaching" | "blocked";

const APPROACHING_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Computes the lock state of an environment based on its lockdown_at timestamp.
 * Returns "normal" if no lockdown_at is set or lockdown is far away (>24h),
 * "approaching" if lockdown is within 24h, or "blocked" if lockdown_at is in the past.
 */
export function getLockState(lockdownAt: PlainMessage<Timestamp> | undefined, now: Date = new Date()): LockState {
    if (!lockdownAt) {
        return "normal";
    }

    const lockdownTime = new Date(Number(lockdownAt.seconds) * 1000 + lockdownAt.nanos / 1_000_000);
    const remainingMs = lockdownTime.getTime() - now.getTime();

    if (remainingMs <= 0) {
        return "blocked";
    }
    if (remainingMs <= APPROACHING_THRESHOLD_MS) {
        return "approaching";
    }
    return "normal";
}

/**
 * Returns a human-readable remaining time string based on lockdownAt.
 * Examples: "5d", "18h", "2h", "30m"
 * Returns null when lockdownAt is undefined or already in the past.
 */
export function getRemainingTime(lockdownAt: PlainMessage<Timestamp> | undefined, now: Date = new Date()): string | null {
    if (!lockdownAt) {
        return null;
    }

    const lockdownTime = new Date(Number(lockdownAt.seconds) * 1000 + lockdownAt.nanos / 1_000_000);
    const remainingMs = lockdownTime.getTime() - now.getTime();

    if (remainingMs <= 0) {
        return null;
    }

    const remainingHours = remainingMs / (1000 * 60 * 60);
    if (remainingHours < 1) {
        const mins = Math.max(1, Math.ceil(remainingMs / (1000 * 60)));
        return `${mins}m`;
    }
    if (remainingHours <= 24) {
        return `${Math.ceil(remainingHours)}h`;
    }

    const remainingDays = Math.ceil(remainingHours / 24);
    return `${remainingDays}d`;
}
